import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  Fade,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageSEO from '../components/PageSEO';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  LocalFireDepartment as FireIcon,
  Diamond as DiamondIcon,
} from '@mui/icons-material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  getProjects, 
  getCategories, 
  getFeaturedProjects,
  ProjectWithDetails, 
  Category, 
  ProjectFilters 
} from '../lib/api/projects';
import { checkItemOwnership, getPromotionStatus } from '../lib/api/promotionBids';
import { hasProjectAccess } from '../lib/api/projectAccess';
import PromotionModal from '../components/PromotionModal';
import ProjectAccessModal from '../components/ProjectAccessModal';

const FindWorkPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [featuredProjects, setFeaturedProjects] = useState<ProjectWithDetails[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  
  // Filters state
  const [filters, setFilters] = useState<ProjectFilters>({
    search: '',
    category_id: '',
    budget_min: undefined,
    budget_max: undefined,
    skills: [],
    featured: false,
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    limit: 12
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [promotedProjects, setPromotedProjects] = useState<Set<string>>(new Set());
  const [projectOwnership, setProjectOwnership] = useState<Set<string>>(new Set());
  const [promotionModal, setPromotionModal] = useState<{
    open: boolean;
    itemId: string;
    itemTitle: string;
  }>({ open: false, itemId: '', itemTitle: '' });
  
  const [projectAccessModal, setProjectAccessModal] = useState<{
    open: boolean;
    projectId: string;
    projectTitle: string;
  }>({ open: false, projectId: '', projectTitle: '' });
  
  const [projectAccess, setProjectAccess] = useState<Set<string>>(new Set());

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load projects when filters change
  useEffect(() => {
    loadProjects();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [categoriesData, featuredData] = await Promise.all([
        getCategories(),
        getFeaturedProjects(6)
      ]);
      
      setCategories(categoriesData);
      setFeaturedProjects(featuredData);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await getProjects(filters);
      
      // Always sort projects to show promoted ones first, then apply other sorting
      let sortedProjects = [...result.projects];
      
      // First, separate promoted and non-promoted projects
      const promotedProjects = sortedProjects.filter(project => project.featured);
      const nonPromotedProjects = sortedProjects.filter(project => !project.featured);
      
      // Apply additional sorting within each group
      if (filters.sort_by === 'budget_min-asc') {
        promotedProjects.sort((a, b) => (a.budget_min || 0) - (b.budget_min || 0));
        nonPromotedProjects.sort((a, b) => (a.budget_min || 0) - (b.budget_min || 0));
      } else if (filters.sort_by === 'budget_min-desc') {
        promotedProjects.sort((a, b) => (b.budget_min || 0) - (a.budget_min || 0));
        nonPromotedProjects.sort((a, b) => (b.budget_min || 0) - (a.budget_min || 0));
      } else if (filters.sort_by === 'views_count-desc') {
        promotedProjects.sort((a, b) => (b.views_count || 0) - (a.views_count || 0));
        nonPromotedProjects.sort((a, b) => (b.views_count || 0) - (a.views_count || 0));
      } else if (filters.sort_by === 'offers_count-desc') {
        promotedProjects.sort((a, b) => (b.offers_count || 0) - (a.offers_count || 0));
        nonPromotedProjects.sort((a, b) => (b.offers_count || 0) - (a.offers_count || 0));
      } else if (filters.sort_by === 'created_at-asc') {
        promotedProjects.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        nonPromotedProjects.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      } else { // created_at-desc (default)
        promotedProjects.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        nonPromotedProjects.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }
      
      // Combine: promoted first, then non-promoted
      sortedProjects = [...promotedProjects, ...nonPromotedProjects];
      
      // Check ownership and promotion status for each project
      const ownershipAndPromotionChecks = await Promise.all(
        result.projects.map(async (project) => {
          const [isOwner, promotionStatus] = await Promise.all([
            checkItemOwnership(project.id, 'PROJECT'),
            getPromotionStatus(project.id, 'PROJECT').catch(() => ({ isPromoted: false }))
          ]);
          return { 
            projectId: project.id, 
            isOwner, 
            isPromoted: promotionStatus.isPromoted 
          };
        })
      );
      
      const ownedProjectIds = new Set(
        ownershipAndPromotionChecks
          .filter(check => check.isOwner)
          .map(check => check.projectId)
      );
      setProjectOwnership(ownedProjectIds);
      
      const promotedProjectIds = new Set(
        ownershipAndPromotionChecks
          .filter(check => check.isPromoted)
          .map(check => check.projectId)
      );
      setPromotedProjects(promotedProjectIds);
      
      // Check project access for each project
      const accessChecks = await Promise.all(
        result.projects.map(async (project) => {
          const hasAccess = await hasProjectAccess(project.id);
          return { projectId: project.id, hasAccess };
        })
      );
      
      const accessibleProjectIds = new Set(
        accessChecks
          .filter(check => check.hasAccess)
          .map(check => check.projectId)
      );
      setProjectAccess(accessibleProjectIds);
      
      setProjects(sortedProjects);
      setTotalPages(result.totalPages);
      setTotalProjects(result.total);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Грешка при зареждане на проектите');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof ProjectFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleSearch = (searchTerm: string) => {
    handleFilterChange('search', searchTerm);
  };

  const handleCategoryChange = (categoryId: string) => {
    handleFilterChange('category_id', categoryId || '');
  };

  const handleBudgetChange = (min?: number, max?: number) => {
    setFilters(prev => ({
      ...prev,
      budget_min: min,
      budget_max: max,
      page: 1
    }));
  };

  const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy as any,
      sort_order: sortOrder,
      page: 1
    }));
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category_id: '',
      budget_min: undefined,
      budget_max: undefined,
      skills: [],
      featured: false,
      sort_by: 'created_at',
      sort_order: 'desc',
      page: 1,
      limit: 12
    });
  };

  const toggleFavorite = (projectId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(projectId)) {
        newFavorites.delete(projectId);
      } else {
        newFavorites.add(projectId);
      }
      return newFavorites;
    });
  };

  const handlePromotionClick = (projectId: string, projectTitle: string) => {
    setPromotionModal({
      open: true,
      itemId: projectId,
      itemTitle: projectTitle
    });
  };

  const handlePromotionSuccess = () => {
    // Reload projects to get updated data
    loadProjects();
  };

  const handleProjectAccessClick = (projectId: string, projectTitle: string) => {
    setProjectAccessModal({
      open: true,
      projectId,
      projectTitle
    });
  };

  const handleProjectAccessGranted = () => {
    // Reload projects to get updated access data
    loadProjects();
  };

  const formatBudget = (min: number | null, max: number | null) => {
    if (!min && !max) return 'Договорна';
    if (!min) return `до €${max?.toLocaleString()}`;
    if (!max) return `от €${min.toLocaleString()}`;
    return `€${min.toLocaleString()} - €${max.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Току-що';
    if (diffInHours < 24) return `преди ${diffInHours}ч`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `преди ${diffInDays}д`;
    return formatDate(dateString);
  };

  if (loading && projects.length === 0) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#0f0f23', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <PageSEO pageKey="findWork" />
      {/* Animated background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
          borderRadius: '20%',
          opacity: 0.4,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Header />
      
      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, px: { xs: 2, sm: 0 } }}>
          <Typography 
            variant="h2" 
            sx={{ 
              color: '#ffffff', 
              fontWeight: 800, 
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('findWork.title')}
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#a0a0a0', 
              mb: 4, 
              maxWidth: 600, 
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 1, sm: 0 }
            }}
          >
            {t('findWork.subtitle')}
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: { xs: 2, md: 4 },
            backdropFilter: 'blur(10px)',
            mb: 4,
            boxShadow: { 
              xs: '0 4px 20px rgba(0, 0, 0, 0.1)',
              md: '0 8px 32px rgba(0, 0, 0, 0.2)'
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Stack spacing={{ xs: 2, md: 3 }}>
              {/* Search Bar */}
              <Box sx={{ 
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(40, 160, 240, 0.05) 100%)',
                  borderRadius: 3,
                  zIndex: -1,
                }
              }}>
                <TextField
                  fullWidth
                  placeholder={t('findWork.searchPlaceholder')}
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ 
                          color: '#28A0F0', 
                          fontSize: { xs: '1.2rem', md: '1.5rem' },
                          filter: 'drop-shadow(0 2px 4px rgba(40, 160, 240, 0.3))'
                        }} />
                      </InputAdornment>
                    ),
                    endAdornment: filters.search && (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={() => handleSearch('')} 
                          size="small"
                          sx={{
                            color: '#a0a0a0',
                            '&:hover': {
                              color: '#ffffff',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      minHeight: { xs: '48px', md: '56px' },
                      borderRadius: 3,
                      '& fieldset': { 
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        borderWidth: 2,
                      },
                      '&:hover fieldset': { 
                        borderColor: '#28A0F0',
                        boxShadow: '0 0 0 1px rgba(40, 160, 240, 0.3)',
                      },
                      '&.Mui-focused fieldset': { 
                        borderColor: '#28A0F0',
                        boxShadow: '0 0 0 2px rgba(40, 160, 240, 0.2)',
                      },
                    },
                    '& .MuiInputBase-input': { 
                      color: '#ffffff',
                      padding: { xs: '12px 14px', md: '16px 14px' },
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.6)',
                        opacity: 1,
                      }
                    },
                  }}
                />
              </Box>

              {/* Filter Controls - Compact Layout */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 2, md: 3 }
              }}>
                {/* Filter Toggle Button - Compact */}
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    borderColor: 'rgba(40, 160, 240, 0.5)',
                    color: '#28A0F0',
                    fontSize: { xs: '0.85rem', md: '0.9rem' },
                    py: { xs: 1, md: 0.8 },
                    px: { xs: 1.5, md: 2 },
                    borderRadius: 2,
                    fontWeight: 500,
                    minWidth: 'auto',
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { sm: '180px', md: '200px' },
                    background: showFilters 
                      ? 'linear-gradient(135deg, rgba(40, 160, 240, 0.15) 0%, rgba(40, 160, 240, 0.05) 100%)'
                      : 'transparent',
                    '&:hover': {
                      borderColor: '#28A0F0',
                      backgroundColor: 'rgba(40, 160, 240, 0.1)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(40, 160, 240, 0.2)',
                    },
                  }}
                >
                  {t('findWork.filters')} {showFilters ? t('findWork.hide') : t('findWork.show')}
                </Button>
                
                {/* Stats and Clear Button - More Space */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 2, md: 2.5 }, 
                  alignItems: { xs: 'stretch', sm: 'center' },
                  flex: 1,
                  justifyContent: { xs: 'center', sm: 'flex-end' }
                }}>
                  {/* Projects Count - Enhanced */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: { xs: 2, sm: 2, md: 2.5 },
                    py: { xs: 1, sm: 0.8, md: 1 },
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(40, 160, 240, 0.05) 100%)',
                    border: '1px solid rgba(40, 160, 240, 0.2)',
                    minWidth: { xs: 'auto', sm: '120px' },
                    justifyContent: 'center',
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#28A0F0',
                        textAlign: 'center',
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                        fontWeight: 600,
                      }}
                    >
                      {t('findWork.projectsCount', { count: totalProjects })}
                    </Typography>
                  </Box>
                  
                  {/* Clear Filters Button - Enhanced */}
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                    sx={{ 
                      color: '#a0a0a0',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      py: { xs: 0.8, md: 0.6 },
                      px: { xs: 1.5, md: 2 },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      minWidth: { xs: 'auto', sm: '120px' },
                      '&:hover': {
                        color: '#ffffff',
                        borderColor: 'rgba(255, 255, 255, 0.4)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    {t('findWork.clearFilters')}
                  </Button>
                </Box>
              </Box>

              {/* Advanced Filters - Enhanced Design */}
              {showFilters && (
                <Fade in={showFilters}>
                  <Box sx={{ 
                    mt: { xs: 1, md: 2 },
                    p: { xs: 2, md: 3 },
                    background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.05) 0%, rgba(40, 160, 240, 0.02) 100%)',
                    borderRadius: 3,
                    border: '1px solid rgba(40, 160, 240, 0.2)',
                    position: 'relative'
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#ffffff', 
                        mb: 3, 
                        fontWeight: 600,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <FilterIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                      {t('findWork.advancedFilters')}
                    </Typography>
                    
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel 
                            sx={{ 
                              color: '#a0a0a0',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              fontWeight: 500,
                              '&.Mui-focused': { color: '#28A0F0' },
                              '&.MuiInputLabel-shrink': { color: '#28A0F0' }
                            }}
                          >
                            {t('findWork.category')}
                          </InputLabel>
                          <Select
                            value={filters.category_id || ''}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            sx={{
                              color: '#ffffff',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              minHeight: { xs: '48px', md: '56px' },
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': { 
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                borderWidth: 2,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 1px rgba(40, 160, 240, 0.3)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 2px rgba(40, 160, 240, 0.2)',
                              },
                            }}
                          >
                            <MenuItem value="" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                              {t('findWork.allCategories')}
                            </MenuItem>
                            {categories.map((category) => (
                              <MenuItem 
                                key={category.id} 
                                value={category.id}
                                sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}
                              >
                                {category.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label={t('findWork.minBudget')}
                          type="number"
                          value={filters.budget_min || ''}
                          onChange={(e) => handleBudgetChange(Number(e.target.value) || undefined, filters.budget_max)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography sx={{ color: '#a0a0a0', fontSize: '0.9rem' }}>€</Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#ffffff',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              minHeight: { xs: '48px', md: '56px' },
                              borderRadius: 2,
                              '& fieldset': { 
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                borderWidth: 2,
                              },
                              '&:hover fieldset': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 1px rgba(40, 160, 240, 0.3)',
                              },
                              '&.Mui-focused fieldset': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 2px rgba(40, 160, 240, 0.2)',
                              },
                            },
                            '& .MuiInputLabel-root': { 
                              color: '#a0a0a0',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              fontWeight: 500,
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label={t('findWork.maxBudget')}
                          type="number"
                          value={filters.budget_max || ''}
                          onChange={(e) => handleBudgetChange(filters.budget_min, Number(e.target.value) || undefined)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography sx={{ color: '#a0a0a0', fontSize: '0.9rem' }}>€</Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#ffffff',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              minHeight: { xs: '48px', md: '56px' },
                              borderRadius: 2,
                              '& fieldset': { 
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                borderWidth: 2,
                              },
                              '&:hover fieldset': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 1px rgba(40, 160, 240, 0.3)',
                              },
                              '&.Mui-focused fieldset': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 2px rgba(40, 160, 240, 0.2)',
                              },
                            },
                            '& .MuiInputLabel-root': { 
                              color: '#a0a0a0',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              fontWeight: 500,
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel 
                            sx={{ 
                              color: '#a0a0a0',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              fontWeight: 500,
                              '&.Mui-focused': { color: '#28A0F0' },
                              '&.MuiInputLabel-shrink': { color: '#28A0F0' }
                            }}
                          >
                            {t('findWork.sortBy')}
                          </InputLabel>
                          <Select
                            value={`${filters.sort_by}-${filters.sort_order}`}
                            onChange={(e) => {
                              const [sortBy, sortOrder] = e.target.value.split('-');
                              handleSortChange(sortBy, sortOrder as 'asc' | 'desc');
                            }}
                            sx={{
                              color: '#ffffff',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              minHeight: { xs: '48px', md: '56px' },
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': { 
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                borderWidth: 2,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 1px rgba(40, 160, 240, 0.3)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                                borderColor: '#28A0F0',
                                boxShadow: '0 0 0 2px rgba(40, 160, 240, 0.2)',
                              },
                            }}
                          >
                            <MenuItem value="created_at-desc" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                              {t('findWork.sortOptions.newest')}
                            </MenuItem>
                            <MenuItem value="created_at-asc" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                              {t('findWork.sortOptions.oldest')}
                            </MenuItem>
                            <MenuItem value="budget_min-asc" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                              {t('findWork.sortOptions.lowestBudget')}
                            </MenuItem>
                            <MenuItem value="budget_min-desc" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                              {t('findWork.sortOptions.highestBudget')}
                            </MenuItem>
                            <MenuItem value="views_count-desc" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                              {t('findWork.sortOptions.mostViewed')}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Featured Projects */}
        {featuredProjects.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <TrendingUpIcon sx={{ color: '#28A0F0', mr: 1 }} />
              <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
                {t('findWork.recommendedProjects')}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {featuredProjects.map((project) => (
                <Grid item xs={12} md={6} lg={4} key={project.id}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)',
                      border: '1px solid rgba(40, 160, 240, 0.3)',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(40, 160, 240, 0.3)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, flex: 1 }}>
                          {project.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={t('findWork.recommended')}
                            size="small"
                            icon={<TrendingUpIcon sx={{ fontSize: '0.8rem' }} />}
                            sx={{
                              backgroundColor: 'rgba(16, 185, 129, 0.2)',
                              color: '#10b981',
                              border: '1px solid #10b981',
                              fontSize: '0.7rem',
                            }}
                          />
                          {promotedProjects.has(project.id) && (
                            <Chip
                              label={t('common.promoted')}
                              size="small"
                              icon={<FireIcon sx={{ fontSize: '0.9rem' }} />}
                              sx={{
                                background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                                color: '#ffffff',
                                border: '2px solid #ff5722',
                                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                height: { xs: 24, sm: 28 },
                                fontWeight: 'bold',
                                px: 1,
                                boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
                                animation: 'pulse 2s infinite, glow 3s infinite',
                                '@keyframes pulse': {
                                  '0%': { transform: 'scale(1)' },
                                  '50%': { transform: 'scale(1.05)' },
                                  '100%': { transform: 'scale(1)' },
                                },
                                '@keyframes glow': {
                                  '0%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                                  '50%': { boxShadow: '0 6px 20px rgba(255, 87, 34, 0.8)' },
                                  '100%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" sx={{ color: '#d0d0d0', mb: 2, lineHeight: 1.6 }}>
                        {projectAccess.has(project.id) ? (
                          project.description.length > 120 
                            ? `${project.description.substring(0, 120)}...` 
                            : project.description
                        ) : (
                          <>
                            {project.description.length > 80 
                              ? `${project.description.substring(0, 80)}...` 
                              : project.description}
                            <Box sx={{ 
                              mt: 1, 
                              p: 2, 
                              background: 'rgba(255, 87, 34, 0.1)', 
                              borderRadius: 1,
                              border: '1px solid rgba(255, 87, 34, 0.3)',
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" sx={{ color: '#ff5722', fontWeight: 600, mb: 1 }}>
                                🔒 За да видите пълното описание и контактите
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleProjectAccessClick(project.id, project.title)}
                                sx={{
                                  background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                                  color: '#ffffff',
                                  fontSize: '0.75rem',
                                  px: 2,
                                  py: 0.5,
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                                  }
                                }}
                              >
                                Закупи достъп за 3 кредита
                              </Button>
                            </Box>
                          </>
                        )}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MoneyIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                          <Typography variant="body2" sx={{ color: '#28A0F0', fontWeight: 600 }}>
                            {formatBudget(project.budget_min, project.budget_max)}
                          </Typography>
                        </Box>
                        {project.deadline && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon sx={{ color: '#a0a0a0', fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                              {formatDate(project.deadline)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {project.client.avatar_url ? (
                            <img
                              src={project.client.avatar_url}
                              alt={project.client.full_name || 'Client'}
                              style={{ width: 24, height: 24, borderRadius: '50%' }}
                            />
                          ) : (
                            <PersonIcon sx={{ color: '#a0a0a0', fontSize: '1.2rem' }} />
                          )}
                          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                            {project.client.full_name || project.client.company || t('findWork.anonymous')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {getTimeAgo(project.created_at)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* All Projects */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <WorkIcon sx={{ color: '#28A0F0', mr: 1 }} />
            <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
              {t('findWork.allProjects')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#28A0F0' }} />
            </Box>
          ) : projects.length === 0 ? (
            <Card
              sx={{
                background: 'rgba(40, 160, 240, 0.1)',
                border: '2px dashed rgba(40, 160, 240, 0.3)',
                borderRadius: 3,
                p: 4,
                textAlign: 'center'
              }}
            >
              <WorkIcon sx={{ fontSize: 60, color: '#28A0F0', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                {t('findWork.noProjects')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {t('findWork.noProjectsDescription')}
              </Typography>
            </Card>
          ) : (
            <>
              <Grid container spacing={3}>
                {projects.map((project) => (
                  <Grid item xs={12} md={6} lg={4} key={project.id}>
                    <Card
                      sx={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: 3,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, flex: 1 }}>
                            {project.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {promotedProjects.has(project.id) && (
                              <Chip
                                label={t('common.promoted')}
                                size="small"
                                icon={<FireIcon sx={{ fontSize: '0.9rem' }} />}
                                sx={{
                                  background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                                  color: '#ffffff',
                                  border: '2px solid #ff5722',
                                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                  height: { xs: 24, sm: 28 },
                                  fontWeight: 'bold',
                                  px: 1,
                                  boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
                                  animation: 'pulse 2s infinite, glow 3s infinite',
                                  '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.05)' },
                                    '100%': { transform: 'scale(1)' },
                                  },
                                  '@keyframes glow': {
                                    '0%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                                    '50%': { boxShadow: '0 6px 20px rgba(255, 87, 34, 0.8)' },
                                    '100%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                                  },
                                }}
                              />
                            )}
                            <Tooltip title={t('findWork.addToFavorites')}>
                              <IconButton
                                size="small"
                                onClick={() => toggleFavorite(project.id)}
                                sx={{ color: favorites.has(project.id) ? '#ef4444' : '#a0a0a0' }}
                              >
                                {favorites.has(project.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('findWork.views')}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <VisibilityIcon sx={{ color: '#a0a0a0', fontSize: '1rem' }} />
                                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                  {project.views_count}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Box>
                        
                        <Typography variant="body2" sx={{ color: '#d0d0d0', mb: 2, lineHeight: 1.6 }}>
                          {projectAccess.has(project.id) ? (
                            project.description.length > 120 
                              ? `${project.description.substring(0, 120)}...` 
                              : project.description
                          ) : (
                            <>
                              {project.description.length > 80 
                                ? `${project.description.substring(0, 80)}...` 
                                : project.description}
                              <Box sx={{ 
                                mt: 1, 
                                p: 2, 
                                background: 'rgba(255, 87, 34, 0.1)', 
                                borderRadius: 1,
                                border: '1px solid rgba(255, 87, 34, 0.3)',
                                textAlign: 'center'
                              }}>
                                <Typography variant="body2" sx={{ color: '#ff5722', fontWeight: 600, mb: 1 }}>
                                  🔒 За да видите пълното описание и контактите
                                </Typography>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleProjectAccessClick(project.id, project.title)}
                                  sx={{
                                    background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                                    color: '#ffffff',
                                    fontSize: '0.75rem',
                                    px: 2,
                                    py: 0.5,
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                                    }
                                  }}
                                >
                                  Закупи достъп за 3 кредита
                                </Button>
                              </Box>
                            </>
                          )}
                        </Typography>
                        
                        {project.category && (
                          <Chip
                            label={project.category.name}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(40, 160, 240, 0.15)',
                              color: '#28A0F0',
                              border: '1px solid rgba(40, 160, 240, 0.3)',
                              fontSize: '0.7rem',
                              mb: 2,
                            }}
                          />
                        )}
                        
                        {project.skills.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {project.skills.slice(0, 3).map((skill, index) => (
                              <Chip
                                key={index}
                                label={skill}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  color: '#ffffff',
                                  fontSize: '0.7rem',
                                }}
                              />
                            ))}
                            {project.skills.length > 3 && (
                              <Chip
                                label={`+${project.skills.length - 3}`}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  color: '#a0a0a0',
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MoneyIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ color: '#28A0F0', fontWeight: 600 }}>
                              {formatBudget(project.budget_min, project.budget_max)}
                            </Typography>
                          </Box>
                          {project.deadline && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimeIcon sx={{ color: '#a0a0a0', fontSize: '1.2rem' }} />
                              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                {formatDate(project.deadline)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {project.client.avatar_url ? (
                              <img
                                src={project.client.avatar_url}
                                alt={project.client.full_name || 'Client'}
                                style={{ width: 24, height: 24, borderRadius: '50%' }}
                              />
                            ) : (
                              <PersonIcon sx={{ color: '#a0a0a0', fontSize: '1.2rem' }} />
                            )}
                            <Box>
                              <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                                {project.client.full_name || project.client.company || t('findWork.anonymous')}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <StarIcon sx={{ color: '#fbbf24', fontSize: '0.8rem' }} />
                                <Typography variant="body2" sx={{ color: '#a0a0a0', fontSize: '0.8rem' }}>
                                  {project.client.rating_avg.toFixed(1)} ({project.client.rating_cnt})
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                            {getTimeAgo(project.created_at)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Button
                            variant="outlined"
                            onClick={() => navigate(`/project/${project.id}`)}
                            sx={{
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: '#ffffff',
                              minWidth: 'auto',
                              px: 2,
                            }}
                          >
                            Детайли
                          </Button>
                          {projectOwnership.has(project.id) && (
                            <Tooltip title={promotedProjects.has(project.id) ? t('common.managePromotion') : t('common.promoteProject')}>
                              <IconButton
                                onClick={() => handlePromotionClick(project.id, project.title)}
                                sx={{
                                  color: promotedProjects.has(project.id) ? '#ff5722' : '#a0a0a0',
                                  border: promotedProjects.has(project.id) ? '1px solid #ff5722' : '1px solid rgba(255, 255, 255, 0.3)',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  '&:hover': {
                                    backgroundColor: promotedProjects.has(project.id) ? 'rgba(255, 87, 34, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                                  }
                                }}
                              >
                                {promotedProjects.has(project.id) ? <FireIcon sx={{ fontSize: '1rem' }} /> : <DiamondIcon sx={{ fontSize: '1rem' }} />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#ffffff',
                        '&.Mui-selected': {
                          backgroundColor: '#28A0F0',
                          color: '#ffffff',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(40, 160, 240, 0.1)',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>

      <Footer />

      {/* Promotion Modal */}
      <PromotionModal
        open={promotionModal.open}
        onClose={() => setPromotionModal({ open: false, itemId: '', itemTitle: '' })}
        itemId={promotionModal.itemId}
        itemType="PROJECT"
        itemTitle={promotionModal.itemTitle}
        onPromotionSuccess={handlePromotionSuccess}
      />

      {/* Project Access Modal */}
      <ProjectAccessModal
        open={projectAccessModal.open}
        onClose={() => setProjectAccessModal({ open: false, projectId: '', projectTitle: '' })}
        projectId={projectAccessModal.projectId}
        projectTitle={projectAccessModal.projectTitle}
        onAccessGranted={handleProjectAccessGranted}
      />
    </Box>
  );
};

export default FindWorkPage;
